import { useContext, useEffect, useState } from 'react';
import GqlClientContext from 'contexts/GqlClientContext';
import getValidAggregateFields from 'components/Grid/getValidAggregateFields';
import getGridTotalsRow from 'api/getGridTotalsRow';
import useGetDatasetDefinition from 'hooks/useGetDatasetDefinition';
import { TOTALS_QUERY } from 'components/Grid/gridConstants';
import BoardContext from 'contexts/BoardContext';
import useDateScope from 'hooks/useDateScope';
import isPerformanceBoard from 'isPerformanceBoard';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';
import EntityDetailsContext from '../../../screens/EntityDetailsShow/EntityDetailsContext';
import { DefaultDatasetFiltersContext } from '../../../contextProviders/SplashScreenProviders/DatasetProviders/DefaultDatasetFiltersProvider';

const useGetFieldsWhichHaveTargets = ({
  baseView,
}: {
  baseView: FleetOps.BaseView | undefined;
}) => {
  const { client } = useContext(GqlClientContext);
  const { board } = useContext(BoardContext);
  const { quickFilters, scope, drillDowns, isDefaultFiltersDisabled } =
    useContext(FilterPlatesContext);
  const { defaultDatasetFiltersLookup } = useContext(
    DefaultDatasetFiltersContext,
  );
  const entityDetails = useContext(EntityDetailsContext);
  const getDatasetDefinition = useGetDatasetDefinition();
  const dateScope = useDateScope({});
  const platesToFilterInput = usePlatesToFilterInput();
  const [totalsRow, setTotalsRow] = useState<any>();

  useEffect(() => {
    if (!baseView || !isPerformanceBoard(board)) {
      return;
    }
    const fields = getValidAggregateFields({
      baseView,
      getDatasetDefinition,
    });

    let isActive = true;

    const filters: FilterInput[] = [
      platesToFilterInput(board.plates),
      platesToFilterInput(scope),
      platesToFilterInput(drillDowns),
      ...quickFilters,
    ].filter((f) => Object.keys(f).length > 0);

    if (entityDetails?.entityFilter) {
      filters.push(entityDetails.entityFilter);
    }

    if (!isDefaultFiltersDisabled) {
      const datasetFilter = defaultDatasetFiltersLookup[board.dataType];
      if (datasetFilter) {
        filters.push(datasetFilter.filterInput);
      }
    }

    getGridTotalsRow({
      query: TOTALS_QUERY,
      dateScope,
      dataType: board.dataType,
      filters,
      fields,
      client,
    }).then((totals: any) => {
      if (!isActive) {
        return;
      }
      const row = totals[0];
      if (!row) {
        setTotalsRow(undefined);
        return;
      }

      const fields = Object.keys(row) as string[];
      fields.forEach((f) => {
        const parts = f.split('.');
        if (parts.length === 2) {
          if (!row[parts[0]]) {
            row[parts[0]] = {};
          }
          row[parts[0]][parts[1]] = row[f];
        }
      });

      setTotalsRow([row]);
    });

    return () => {
      isActive = false;
    };
  }, [
    baseView,
    board,
    client,
    dateScope,
    defaultDatasetFiltersLookup,
    getDatasetDefinition,
    drillDowns,
    entityDetails?.entityFilter,
    isDefaultFiltersDisabled,
    platesToFilterInput,
    quickFilters,
    scope,
  ]);

  return totalsRow;
};

export default useGetFieldsWhichHaveTargets;
